/**
 * Tengill: tap the source → navigate (fingers scroll) and tap the target → optional label → saved
 * as a connector object; or change the target of an existing one. The reading position from the
 * start of the flow is restored when it ends.
 */
import { useCallback, useMemo, useRef, useState, type RefObject } from 'react';
import { DEFAULT_CONNECTOR_SIZE, MAX_LABEL_CHARS, quant, unquant, type AnnotationOf, type ConnectorData } from '../../shared/annotations';
import type { SheetAnnotations } from '../annotations/contract';
import { useToast } from '../components/toast';
import { useT } from '../i18n';
import type { PagePoint, ViewerHandle, ViewerPosition } from '../viewer/types';
import { suggestConnectorLabels } from './connectorLabels';
import type { AddObject } from './useTextBox';

export interface ConnectorFlow {
  /** 'target': navigate and tap the target; 'label': the optional label sheet is open. */
  step: 'target' | 'label';
  source: PagePoint;
  target: PagePoint | null;
  /** Set when changing the target of an existing connector. */
  editId: string | null;
  /** Reading position when the flow started (restored when it ends). */
  returnTo: ViewerPosition | null;
}

export function useConnectorFlow({
  engine,
  engineRef,
  handleRef,
  addObject,
  color,
  size,
}: {
  engine: SheetAnnotations;
  engineRef: RefObject<SheetAnnotations>;
  handleRef: RefObject<ViewerHandle | null>;
  addObject: AddObject;
  /** Colour and pin size of new connectors (tool preferences). */
  color: string;
  size: number;
}) {
  const t = useT();
  const toast = useToast();
  const [flow, setFlowState] = useState<ConnectorFlow | null>(null);
  const flowRef = useRef<ConnectorFlow | null>(null);
  const setFlow = useCallback((f: ConnectorFlow | null) => {
    flowRef.current = f;
    setFlowState(f);
  }, []);
  const colorRef = useRef(color);
  colorRef.current = color;
  const sizeRef = useRef(size);
  sizeRef.current = size;

  const finish = useCallback(
    (f: ConnectorFlow) => {
      setFlow(null);
      if (f.returnTo) handleRef.current?.restorePosition(f.returnTo);
    },
    [setFlow, handleRef],
  );

  const cancel = useCallback(() => {
    const f = flowRef.current;
    if (f) finish(f);
  }, [finish]);

  /** Source tapped with the Tengill tool. */
  const start = useCallback(
    (source: PagePoint) => setFlow({ step: 'target', source, target: null, editId: null, returnTo: handleRef.current?.getPosition() ?? null }),
    [setFlow, handleRef],
  );

  /** "Breyta áfangastað" on a selected own connector. */
  const retarget = useCallback(
    (c: AnnotationOf<'connector'>) =>
      setFlow({
        step: 'target',
        source: { page: c.page, x: unquant(c.data.at[0]), y: unquant(c.data.at[1]) },
        target: null,
        editId: c.id,
        returnTo: handleRef.current?.getPosition() ?? null,
      }),
    [setFlow, handleRef],
  );

  const chooseTarget = useCallback(
    (pt: PagePoint) => {
      const f = flowRef.current;
      if (!f) return;
      if (!f.editId) {
        setFlow({ ...f, step: 'label', target: pt });
        return;
      }
      const eng = engineRef.current;
      const o = eng.get(f.editId);
      if (o && o.kind === 'connector' && o.deletedAt === null) {
        eng.apply([{ type: 'update', id: o.id, data: { ...o.data, target: { page: pt.page, at: [quant(pt.x), quant(pt.y)] } } }]);
        toast.show(t('annotate.conn_target_changed'), { tone: 'ok' });
      }
      finish(f);
    },
    [engineRef, finish, setFlow, toast, t],
  );

  const save = useCallback(
    (label: string | null) => {
      const f = flowRef.current;
      if (!f || !f.target) return;
      const l = (label ?? '').trim().slice(0, MAX_LABEL_CHARS);
      const data: ConnectorData = {
        at: [quant(f.source.x), quant(f.source.y)],
        target: { page: f.target.page, at: [quant(f.target.x), quant(f.target.y)] },
        color: colorRef.current,
        ...(l ? { label: l } : {}),
        // The default size is left out: such a pin still shows on an iPad that has not updated yet.
        ...(sizeRef.current !== DEFAULT_CONNECTOR_SIZE ? { size: sizeRef.current } : {}),
      };
      const saved = addObject(f.source.page, 'connector', data);
      finish(f);
      if (saved) toast.show(t('annotate.conn_saved'), { tone: 'ok' });
    },
    [addObject, finish, toast, t],
  );

  const labelSuggestions = useMemo(() => {
    if (!flow || flow.step !== 'label' || !flow.target) return [];
    const used = engine.connectors
      .filter((c) => c.data.label && engine.isMine(c.id))
      .sort((a, b) => b.hlcMs - a.hlcMs)
      .map((c) => c.data.label!);
    return suggestConnectorLabels({
      source: flow.source,
      target: flow.target,
      used,
      forwardWord: t('annotate.conn_label_forward'),
      verseWord: t('annotate.conn_label_verse'),
    });
  }, [flow, engine, t]);

  const reset = useCallback(() => setFlow(null), [setFlow]);

  return { flow, flowRef, start, retarget, chooseTarget, save, cancel, reset, labelSuggestions };
}
